import { useState } from "react";
import axios from "axios";
import {
  CheckCircle2, Clock, AlertCircle, ListTodo, CircleDot, Ban, FileText,
  ArrowRight, Eye, Zap, Loader2,
} from "lucide-react";
import { STAGE_STATUS, TASK_STATUS_ICON, API } from "./constants";

export const ProjectOverview = ({ projectId, tasks, metrics, stages, milestones, activities, sessions, setTab }) => {
  const [stageInsights, setStageInsights] = useState({});
  const [insightsLoading, setInsightsLoading] = useState({});

  const fetchStageInsights = async (stageName) => {
    if (insightsLoading[stageName]) return;
    setInsightsLoading(p => ({ ...p, [stageName]: true }));
    try {
      const { data: d } = await axios.post(`${API}/projects/${projectId}/stage-insights`, { stage: stageName }, { withCredentials: true });
      setStageInsights(p => ({ ...p, [stageName]: d.insights }));
    } catch (e) { console.error(e); }
    finally { setInsightsLoading(p => ({ ...p, [stageName]: false })); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="overview-tab">
      {/* Metrics Cards */}
      <div className="grid grid-cols-6 gap-3">
        {[
          { label: "Tasks", val: metrics?.total_tasks || 0, icon: ListTodo, color: "text-[#014d7d]", bg: "bg-[#014d7d]/5" },
          { label: "Completed", val: metrics?.completed_tasks || 0, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "In Progress", val: metrics?.in_progress_tasks || 0, icon: CircleDot, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Blocked", val: metrics?.blocked_tasks || 0, icon: Ban, color: "text-red-500", bg: "bg-red-50" },
          { label: "Overdue", val: metrics?.overdue_tasks || 0, icon: AlertCircle, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Artifacts", val: metrics?.total_artifacts || 0, icon: FileText, color: "text-purple-600", bg: "bg-purple-50" },
        ]?.map(s => (
          <div key={s.label} className="bg-white border border-border/50 rounded-xl p-4">
            <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center mb-2`}><s.icon className={`w-4 h-4 ${s.color}`} /></div>
            <p className="text-xl font-heading font-bold text-foreground">{s.val}</p>
            <p className="text-[10px] text-foreground/50 font-semibold uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Two-column: Recent Tasks + Timeline */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-border/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider">Recent Tasks</h3>
            <button onClick={() => setTab("tasks")} className="text-[10px] text-[#014d7d] font-semibold hover:underline">View all</button>
          </div>
          <div className="space-y-1.5">
            {tasks.slice(0, 5)?.map(t => (
              <div key={t.id} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-foreground/[0.02]">
                {TASK_STATUS_ICON[t.status] || TASK_STATUS_ICON.todo}
                <span className="text-xs font-medium text-foreground truncate flex-1">{t.title}</span>
                {t.assignee && <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[7px] font-bold" style={{ background: t.assignee.color || "#999" }}>{t.assignee.initials}</div>}
              </div>
            ))}
            {tasks.length === 0 && <p className="text-xs text-foreground/45 text-center py-4">No tasks yet</p>}
          </div>
        </div>
        <div className="bg-white border border-border/50 rounded-xl p-4">
          <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Timeline & Milestones</h3>
          <div className="space-y-2">
            {(milestones || [])?.map((m, i) => (
              <div key={m.id || i} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-foreground/[0.02]">
                <div className={`w-2 h-2 rounded-full shrink-0 ${m.status === "completed" ? "bg-emerald-500" : m.status === "in_progress" ? "bg-[#014d7d]" : "bg-gray-300"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{m.title}</p>
                  {m.stage && <p className="text-[10px] text-foreground/50">{m.stage}</p>}
                </div>
                {m.due_date && <span className="text-[10px] text-foreground/50 shrink-0">{new Date(m.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-md ${m.status === "completed" ? "bg-emerald-50 text-emerald-600" : "bg-gray-50 text-foreground/50"}`}>{m.status}</span>
              </div>
            ))}
            {(!milestones || milestones.length === 0) && <p className="text-xs text-foreground/45 text-center py-4">No milestones set</p>}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-border/50 rounded-xl p-4">
        <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Recent Activity</h3>
        <div className="space-y-2">
          {(activities || []).slice(0, 6)?.map((a, i) => (
            <div key={a.id || i} className="flex items-center gap-3 text-xs text-foreground/50">
              <div className="w-1.5 h-1.5 rounded-full bg-[#014d7d]/30 shrink-0" />
              <span className="flex-1 truncate">{a.description}</span>
              <span className="text-[10px] text-foreground/45 shrink-0">{new Date(a.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI Stage Insights */}
      {stages.length > 0 && (
        <div className="bg-white border border-border/50 rounded-xl p-4" data-testid="stage-insights-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold text-foreground/50 uppercase tracking-wider">AI Stage Intelligence</h3>
            <span className="text-[10px] text-foreground/45">Click a stage to get AI-powered insights</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {stages?.map(s => {
              const ins = stageInsights[s.name];
              const isLoading = insightsLoading[s.name];
              const healthColors = { healthy: "bg-emerald-50 border-emerald-200 text-emerald-700", at_risk: "bg-amber-50 border-amber-200 text-amber-700", blocked: "bg-red-50 border-red-200 text-red-700" };
              return (
                <div key={s.name} className="border border-border/50 rounded-xl overflow-hidden" data-testid={`insights-stage-${s.name.toLowerCase()}`}>
                  <button onClick={() => fetchStageInsights(s.name)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-foreground/[0.01] transition-colors" data-testid={`insights-btn-${s.name.toLowerCase()}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${(STAGE_STATUS[s.status] || STAGE_STATUS.pending).bg}`} />
                      <span className="text-xs font-bold text-foreground">{s.name}</span>
                      {ins?.stage_health && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md border ${healthColors[ins.stage_health] || healthColors.at_risk}`}>{ins.stage_health.replace("_", " ").toUpperCase()}</span>}
                    </div>
                    {isLoading ? <Loader2 className="w-3 h-3 animate-spin text-foreground/50" /> : <Zap className="w-3 h-3 text-[#014d7d]/40" />}
                  </button>
                  {ins && (
                    <div className="px-4 pb-3 space-y-2 border-t border-border/30">
                      <p className="text-[11px] text-foreground/50 pt-2 font-medium">{ins.summary}</p>
                      {ins.next_steps?.length > 0 && (
                        <div>
                          <p className="text-[9px] font-bold text-foreground/45 uppercase mb-1">Next Steps</p>
                          {ins.next_steps.slice(0, 3)?.map((step, i) => (
                            <div key={i} className="flex items-start gap-1.5 text-[11px] text-foreground/50"><ArrowRight className="w-2.5 h-2.5 mt-0.5 text-[#014d7d] shrink-0" /><span>{step}</span></div>
                          ))}
                        </div>
                      )}
                      {ins.missing_elements?.length > 0 && ins.missing_elements[0] !== "No gaps detected" && (
                        <div>
                          <p className="text-[9px] font-bold text-amber-500 uppercase mb-1">Missing</p>
                          {ins.missing_elements.slice(0, 2)?.map((m, i) => (
                            <div key={i} className="text-[11px] text-amber-600/70 flex items-start gap-1.5"><AlertCircle className="w-2.5 h-2.5 mt-0.5 shrink-0" /><span>{m}</span></div>
                          ))}
                        </div>
                      )}
                      {ins.risks?.length > 0 && ins.risks[0] !== "No major risks identified" && (
                        <div>
                          <p className="text-[9px] font-bold text-red-500 uppercase mb-1">Risks</p>
                          {ins.risks.slice(0, 2)?.map((r, i) => (
                            <div key={i} className="text-[11px] text-red-500/70 flex items-start gap-1.5"><Ban className="w-2.5 h-2.5 mt-0.5 shrink-0" /><span>{r}</span></div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
